import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { onboardingChannels, onboardingHints, onboardingResources, onboardingSteps } from "./onboarding-meta";
import { useAuth } from "./auth";
import { useAppNav } from "./app-nav";
import { api, apiErrorText } from "@/lib/api";
import { t } from "@/lib/i18n";

/* Живой макет окна «Начало работы» (Onboarding checklist):
   приветствие, полоса прогресса, четыре раскрывающихся шага и панель «Ресурсы» справа. */

// кнопка шага отмечает его и открывает нужный раздел
const STEP_SECTION: Record<string, string> = { channels: "channels", lifecycle: "lifecycle", ai: "aiassist", team: "usersettings" };

function OnboardingDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [open, setOpen] = useState<string | null>(onboardingSteps[0]?.id ?? null);
  const { user } = useAuth();
  const nav = useAppNav();
  const firstName = (user?.name || "").split(/\s+/)[0] || t("коллега");
  const [done, setDone] = useState<string[]>([]);
  // шаги, засчитанные сервером сами (например, «Пригласите коллег» — в пространстве уже больше одного человека)
  const [auto, setAuto] = useState<string[]>([]);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    api<{ done: string[]; auto: string[] }>("/onboarding").then(
      (r) => {
        setDone(r.done);
        setAuto(r.auto);
      },
      () => undefined,
    );
  }, []);

  const mark = async (stepId: string, value: boolean) => {
    const r = await api<{ done: string[]; auto: string[] }>(`/onboarding/${stepId}`, { method: "PUT", json: { done: value } });
    setDone(r.done);
    setAuto(r.auto);
    return r;
  };

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  const complete = (stepId: string) => {
    const step = onboardingSteps.find((s) => s.id === stepId);
    setDone((prev) => (prev.includes(stepId) ? prev : [...prev, stepId]));
    setOpen(null);
    mark(stepId, true).then(
      () => (STEP_SECTION[stepId] ? nav.open(STEP_SECTION[stepId]) : flash(t("Шаг «{step}» отмечен выполненным", { step: step?.cta ?? "" }))),
      (err) => flash(apiErrorText(err)),
    );
  };

  const undo = (stepId: string) => {
    if (auto.includes(stepId)) {
      flash(t("Этот шаг засчитан автоматически — он уже выполнен в рабочем пространстве"));
      return;
    }
    setDone((prev) => prev.filter((id) => id !== stepId));
    mark(stepId, false).then(
      () => flash(t("Шаг снова в работе — прогресс пересчитался")),
      (err) => flash(apiErrorText(err)),
    );
  };

  const progress = Math.round((done.length / onboardingSteps.length) * 100);
  const allDone = done.length === onboardingSteps.length;

  return (
    <Band id="onboarding" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("рабочее пространство → начало работы")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Чеклист: {done} из {total}", { done: done.length, total: onboardingSteps.length })}
          </span>
        </div>


        <div className="p-3.5 md:p-5">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
            {/* ── чеклист ── */}
            <div className="min-w-0">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-[15px] font-semibold leading-snug">
                    <span aria-hidden>👋</span> {t("Привет, {name}! Давайте начнём.", { name: firstName })}
                  </h3>
                  <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                    {allDone
                      ? t("Все четыре шага пройдены — входящие, воронка и ассистент готовы к работе.")
                      : t("Четыре шага, чтобы входящие заработали на всю команду.")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="relative block h-1.5 w-28 overflow-hidden rounded-full bg-foreground/10 md:w-36" aria-hidden>
                    <span className="block h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
                  </span>
                  <span className="text-[11px] tabular-nums text-muted-foreground">
                    {t("{done} из {total}", { done: done.length, total: onboardingSteps.length })}
                  </span>
                </div>
              </div>

              <ul className="mt-3 flex flex-col gap-2">
                {onboardingSteps.map((step) => {
                  const completeStep = done.includes(step.id);
                  const expanded = open === step.id;
                  return (
                    <li
                      key={step.id}
                      className={cn(
                        "overflow-hidden rounded-btn border transition",
                        expanded ? "border-primary/45 bg-background/60" : "border-border bg-background/40",
                        completeStep && !expanded && "border-primary/30 bg-primary/[.04]",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => setOpen(expanded ? null : step.id)}
                        aria-expanded={expanded}
                        className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2.5 text-start"
                      >
                        <span
                          className={cn(
                            "grid size-5 flex-none place-items-center rounded-full border transition",
                            completeStep ? "border-primary bg-primary text-primary-foreground" : "border-border text-transparent",
                          )}
                          aria-hidden
                        >
                          <Check className="size-3" strokeWidth={3} />
                        </span>
                        <span
                          className={cn(
                            "min-w-0 flex-1 text-[12.5px] font-semibold leading-snug",
                            completeStep && "text-muted-foreground",
                          )}
                        >
                          {step.title}
                        </span>
                        {completeStep ? (
                          <span className="hidden flex-none rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary sm:inline">
                            {t("Выполнено")}
                          </span>
                        ) : null}
                        <ChevronDown
                          className={cn("size-4 flex-none text-muted-foreground transition", expanded && "rotate-180")}
                          aria-hidden
                        />
                      </button>

                      {expanded ? (
                        <div className="border-t border-border/70 px-3 py-3">
                          <div className={cn("grid gap-3", step.visual && "md:grid-cols-[minmax(0,1fr)_10.5rem]")}>
                            <div className="min-w-0">
                              <p className="text-[11.5px] leading-relaxed text-muted-foreground">{step.text}</p>
                              {step.bullets.length > 0 ? (
                                <ul className="mt-2 flex flex-col gap-1">
                                  {step.bullets.map((b) => (
                                    <li key={b} className="flex items-start gap-1.5 text-[11px] text-foreground/75">
                                      <i className="ri-check-line mt-[2px] text-[12px] leading-none text-primary" aria-hidden />
                                      {b}
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => complete(step.id)}
                                  className="inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                                >
                                  {step.cta}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpen(null);
                                    flash(t("Шаг «{step}» отложен — вернётесь к нему позже", { step: step.cta }));
                                  }}
                                  className="inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                                >
                                  {t("Позже")}
                                </button>
                              </div>
                            </div>

                            {step.visual ? (
                              <div className="relative hidden place-items-center overflow-hidden rounded-btn border border-border bg-primary/[.07] p-3 md:grid">
                                <div className="absolute -end-6 -top-6 size-16 rounded-full bg-primary/10" aria-hidden />
                                <div className="relative grid grid-cols-3 gap-2">
                                  {onboardingChannels.map((c) => (
                                    <span
                                      key={c.label}
                                      title={c.label}
                                      className="grid size-8 place-items-center rounded-full border border-border bg-card text-primary"
                                    >
                                      <i className={cn(c.icon, "text-[13px] leading-none")} aria-hidden />
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                      {completeStep && !expanded ? (
                        <div className="flex flex-wrap items-center gap-2 border-t border-border/60 px-3 py-1.5 sm:hidden">
                          <span className="text-[10.5px] font-semibold text-primary">{t("Выполнено")}</span>
                          <button
                            type="button"
                            onClick={() => undo(step.id)}
                            className="cursor-pointer text-[10.5px] text-muted-foreground underline decoration-dotted hover:text-foreground"
                          >
                            {t("Отменить")}
                          </button>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>

              {done.length > 0 ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-[10.5px] text-muted-foreground">{t("Выполнено: {done} из {total}", { done: done.length, total: onboardingSteps.length })}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(onboardingSteps[0]?.id ?? null);
                      Promise.all(done.filter((id) => !auto.includes(id)).map((id) => mark(id, false))).then(
                        () => flash(t("Прогресс сброшен — чеклист снова с начала")),
                        (err) => flash(apiErrorText(err)),
                      );
                    }}
                    className="inline-flex cursor-pointer items-center gap-1 text-[10.5px] text-muted-foreground underline decoration-dotted hover:text-foreground"
                  >
                    <i className="ri-refresh-line text-[12px] leading-none" aria-hidden />
                    {t("Сбросить прогресс")}
                  </button>
                </div>
              ) : null}
            </div>

            {/* ── ресурсы ── */}
            <aside className="min-w-0 rounded-btn border border-border bg-background/50 p-3">
              <p className="text-[12px] font-semibold leading-snug">{t("Ресурсы")}</p>
              <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">
                {t("Подсказки и помощь, если что-то не получается.")}
              </p>
              <ul className="mt-2 flex flex-col gap-0.5">
                {onboardingResources.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => flash(t("«{label}» появится позже — пока пишите через «Идея / Проблема» внизу экрана", { label: r.label }))}
                      className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start text-[11.5px] text-muted-foreground transition hover:bg-muted hover:text-foreground"
                    >
                      <i className={cn(r.icon, "flex-none text-[13px] leading-none text-primary")} aria-hidden />
                      <span className="min-w-0 flex-1">{r.label}</span>
                      <i className="ri-arrow-right-s-line flex-none text-[12px] leading-none opacity-50" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          </div>

          <div className="mt-4 grid gap-2 border-t border-border/60 pt-3 sm:grid-cols-3">
            {onboardingHints.map((h) => (
              <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                  <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                  {h.title}
                </p>
                <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
              </div>
            ))}
          </div>

          <p className="mt-3 text-[11px] text-muted-foreground">
            {t("Отметки шагов сохраняются в вашем профиле; часть шагов засчитывается сама, когда они выполнены в пространстве.")}
          </p>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default OnboardingDemo;